import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getJobs } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Brain, Search, MapPin, Clock, ArrowLeft, Briefcase } from "lucide-react";
import BrandedHeader from "@/components/BrandedHeader";

export default function CandidateJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const res = await getJobs(true);
      setJobs(res.data);
    } catch (e) {
      console.error("Failed to load jobs", e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = jobs.filter(j => {
    const isActive = j.is_active !== false;  // Only show active jobs
    const matchesSearch = !search || j.title.toLowerCase().includes(search.toLowerCase()) || j.description.toLowerCase().includes(search.toLowerCase());
    const matchesType = !filterType || j.role_type === filterType;
    return isActive && matchesSearch && matchesType;
  });

  const roleTypeColors = {
    Sales: "bg-[hsl(var(--chart-1))]/15 text-[hsl(var(--chart-1))] border-[hsl(var(--chart-1))]/25",
    Technical: "bg-[hsl(var(--chart-4))]/15 text-[hsl(var(--chart-4))] border-[hsl(var(--chart-4))]/25",
    Operations: "bg-[hsl(var(--chart-3))]/15 text-[hsl(var(--chart-3))] border-[hsl(var(--chart-3))]/25",
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Branded Header */}
      <BrandedHeader>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Briefcase className="w-6 h-6 text-primary" />
          <span className="font-display text-lg font-semibold">Open Positions</span>
        </div>
      </BrandedHeader>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search positions..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-card"
              data-testid="jobs-search-input"
            />
          </div>
          <div className="flex gap-2">
            {["", "Sales", "Technical", "Operations"].map(type => (
              <Button
                key={type}
                variant={filterType === type ? "default" : "secondary"}
                size="sm"
                onClick={() => setFilterType(type)}
                className="btn-press"
                data-testid={`jobs-filter-${type || 'all'}`}
              >
                {type || "All"}
              </Button>
            ))}
          </div>
        </div>

        {/* Jobs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-primary typing-dot" />
              <div className="w-2 h-2 rounded-full bg-primary typing-dot" />
              <div className="w-2 h-2 rounded-full bg-primary typing-dot" />
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No positions found</p>
            <p className="text-xs text-muted-foreground mt-1">Check back later or adjust your filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(job => (
              <Card
                key={job.id}
                className="card-hover cursor-pointer border-border bg-card"
                onClick={() => navigate(`/jobs/${job.id}`)}
                data-testid="job-card"
              >
                <CardHeader className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className={`text-xs border ${roleTypeColors[job.role_type] || ''}`}>
                      {job.role_type}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {job.seniority_level}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg font-display leading-tight">{job.title}</CardTitle>
                  <CardDescription className="text-sm text-muted-foreground line-clamp-2 mt-1">
                    {job.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {job.required_skills.slice(0, 4).map((skill, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
                    ))}
                    {job.required_skills.length > 4 && (
                      <Badge variant="secondary" className="text-xs">+{job.required_skills.length - 4}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {job.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {job.location}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {job.target_duration_minutes || 30} min interview
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
